import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {environment as prodEnvironment} from '../../../environments/environment.prod';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {TorchUser, TorchUserService, TorchTokenService, TorchUserSettingsService} from '../../../@backend/modules';
import {UserSetting} from '../../../@backend/modules/@users/models/user-setting.model';
import {Angulartics2} from 'angulartics2';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  toolbarStockPrice: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  notifyOnUpdates: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  partHomeHealthCharts: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  darkMode: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  isChangingThemes: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  torchToken: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  graphToken: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  tokens: Observable<{ torchToken: string; graphToken: string }>;

  isProduction = environment.production;
  isStaging = environment.staging;
  isDevelopment = environment.development;

  currentTorchUser: BehaviorSubject<TorchUser> = new BehaviorSubject<TorchUser>(null);

  private envFileSocketBackend = environment.filesWS;
  private publicFileSocketBackend = prodEnvironment.filesWS;

  constructor(private tokenService: TorchTokenService,
              private userSettingsService: TorchUserSettingsService,
              protected angulartics2: Angulartics2,
              private userService: TorchUserService) {

    this.tokenService.getTorchToken().subscribe(this.torchToken);
    this.tokenService.getGraphToken().subscribe(this.graphToken);

    this.tokens = combineLatest([this.torchToken, this.graphToken]).pipe(
      map(([torchToken, graphToken]) => ({
        torchToken: torchToken.trim(),
        graphToken: graphToken.trim()
      })),
    );

    this.userSettingsService.settings.subscribe(settings => {
      this.updateSettings(settings);
    });

    this.userService.currentTorchUser.subscribe(torchUser => {
      this.currentTorchUser.next(torchUser);
    });
  }

  private static getSettingValue<T>(setting: UserSetting): T {
    return setting.value as unknown as T;
  }

  updateNotifyUpdates(value: boolean) {
    this.angulartics2.setUserProperties.next({
      notifyUpdates: value
    });

    return this.userSettingsService.updateSetting('notifyUpdates', value);
  }

  updateShowStockPrice(value: boolean) {
    this.angulartics2.setUserProperties.next({
      showStockPrice: value
    });

    return this.userSettingsService.updateSetting('toolbarStockPrice', value);
  }

  updateShowPartHomeHealthCharts(value: boolean) {
    this.angulartics2.setUserProperties.next({
      showPartHomeHealthCharts: value
    });

    return this.userSettingsService.updateSetting('partHomeHealthCharts', value);
  }

  updateDarkMode(value: string) {
    this.angulartics2.setUserProperties.next({
      darkMode: value
    });

    return this.userSettingsService.updateSetting('darkMode', value);
  }

  set useRemoteFileSocketBackend(newValue: boolean) {
    if (newValue) {
      localStorage.setItem('useFileRemoteBackend', 'true');
    } else {
      localStorage.setItem('useFileRemoteBackend', 'false');
    }

    location.reload();
  }

  get useRemoteFileSocketBackend(): boolean {
    const useFileRemoteBackend = localStorage.getItem('useFileRemoteBackend');

    if (!!useFileRemoteBackend) {
      return useFileRemoteBackend === 'true';
    }

    return false;
  }

  get filesSocketBackend(): string {
    return this.useRemoteFileSocketBackend ? this.publicFileSocketBackend : this.envFileSocketBackend;
  }

  get debugWebsockets(): boolean {
    return !!localStorage.getItem('debug');
  }

  set debugWebsockets(newValue: boolean) {
    if (newValue) {
      localStorage.setItem('debug', '*');
    } else {
      localStorage.removeItem('debug');
    }

    location.reload();
  }

  updateSettings(settings: UserSetting[]) {
    if (!!settings) {
      // Toolbar Stock Price
      const toolbarStockPriceSetting = this.findSetting('toolbarStockPrice', settings, true);
      this.toolbarStockPrice.next(SessionService.getSettingValue(toolbarStockPriceSetting));

      // Part Home Health Charts
      const partHomeHealthChartsSetting = this.findSetting('partHomeHealthCharts', settings, true);
      this.partHomeHealthCharts.next(SessionService.getSettingValue(partHomeHealthChartsSetting));

      // Dark mode
      const darkModeSetting = this.findSetting('darkMode', settings, 'theme-phoenix-light');
      this.darkMode.next(SessionService.getSettingValue(darkModeSetting));
      this.isChangingThemes.next(false);

      // Notify updates
      const notifyUpdatesSetting = this.findSetting('notifyUpdates', settings, true);
      this.notifyOnUpdates.next(SessionService.getSettingValue(notifyUpdatesSetting));

      // Lastly, save metrics values
      this.angulartics2.setUserProperties.next({
        notifyUpdates: notifyUpdatesSetting.value,
        showStockPrice: toolbarStockPriceSetting.value,
        darkMode: darkModeSetting.value,
        showPartHomeHealthCharts: partHomeHealthChartsSetting.value
      });
    }
  }

  private findSetting(name: string, inArray: UserSetting[], defaultValue?: boolean | string | number) {
    const setting = inArray.find(s => s.name === name);

    if (!setting && !!defaultValue) {
      return UserSetting.fixedSetting(name, defaultValue);
    }

    return setting;
  }
}
